import {useCallback} from 'react';
import {useDocumentContext} from '@/components/document/context';
import {FieldGrids} from '@/components/document/group';
import {
  GroupModalCustomActions,
  GroupModalNewActions,
  GroupModalProps,
  GroupModalWrapper,
} from '@/components/document/group-modal-wrapper';
import {useGroupModalValidation} from '@/components/document/hooks/use-group-modal-validation';
import {getAction} from '@/engine/actions';
import {ACTION_TYPES} from '@/model/types/constants';

export const BasicGroupModal = ({group, fields, sectionId}: GroupModalProps) => {
  const {valueResolver, store, visibilitySet} = useDocumentContext();

  const closeModal = useCallback(() => {
    const closeModalAction = getAction({type: ACTION_TYPES.CLOSE_MODAL, group: group.id});
    closeModalAction(store, valueResolver, visibilitySet)();
  }, [store, valueResolver, visibilitySet, group.id]);

  const saveAndCloseModal = useCallback(() => {
    // Close the modal, then execute custom onSave actions
    closeModal();
    group.onSave.forEach(action => action(group.id));
  }, [group.id, group.onSave, closeModal]);

  const cancelAndCloseModal = useCallback(() => {
    // Close the modal, then execute custom onCancel actions
    closeModal();
    group.onCancel.forEach(action => action(group.id));
  }, [group.id, group.onCancel, closeModal]);

  const {isGroupValid, validateAllFields} = useGroupModalValidation(group.id);
  const hasNewEntryActions = group.onCancel.length > 0 || group.onSave.length > 0;
  const hasCustomActions = group.buttons.length > 0;

  return (
    <GroupModalWrapper group={group} onClose={cancelAndCloseModal}>
      <FieldGrids group={group} fields={fields} sectionId={sectionId} />

      {hasCustomActions && (
        <div className="mt-5">
          <GroupModalCustomActions buttons={group.buttons} />
        </div>
      )}

      {hasNewEntryActions && (
        <div className="mt-5">
          <GroupModalNewActions
            onSave={saveAndCloseModal}
            onCancel={cancelAndCloseModal}
            isSaveDisabled={!isGroupValid}
            onSaveDisabled={validateAllFields}
          />
        </div>
      )}
    </GroupModalWrapper>
  );
};
